// Market inventory model for the schedule builder's Markets section: turns the
// catalog's `filter_options.market_keys` into searchable rows, scopes them to
// the schedule's sport and groups them Player / Team / Game. Live market
// availability, joined onto these rows, is marketAvailability.js.
//
// Two catalog shapes are accepted. A current server sends
// `{ value, label, participant_type, market_family, stat_category?, aliases?, sports?, leagues?, periods? }`;
// an older one sends only `{ value, label }`, in which case `hasMetadata` is
// false and the section shows one list searched by label and value.
import { filterOptionsByScope } from '../prepackRecipeModel'

const WHITESPACE_PATTERN = /\s+/

export const MARKET_GROUPS = ['player', 'team', 'game']

// Sportsbook shorthand an operator types, mapped to words the catalog labels
// use. The catalog's own `aliases` are searched first; these only fill gaps.
// Expansions with a space match as a phrase from the start of a word.
export const MARKET_SYNONYMS = Object.freeze({
  yd: ['yards'],
  yds: ['yards'],
  yard: ['yards'],
  td: ['touchdown'],
  tds: ['touchdown'],
  atd: ['anytime touchdown'],
  ftd: ['first touchdown'],
  ltd: ['last touchdown'],
  rec: ['receiving', 'receptions'],
  recs: ['receptions'],
  pass: ['passing'],
  rush: ['rushing'],
  int: ['interceptions'],
  ints: ['interceptions'],
  ml: ['moneyline', 'game winner'],
  '1x2': ['moneyline', 'match result'],
  pts: ['points'],
  reb: ['rebounds'],
  rebs: ['rebounds'],
  ast: ['assists'],
  asts: ['assists'],
  pra: ['points rebounds assists'],
  pr: ['points rebounds'],
  pa: ['points assists'],
  ra: ['rebounds assists'],
  stl: ['steals'],
  blk: ['blocks'],
  '3pt': ['3 point', 'three pointers', 'threes'],
  '3pm': ['3 point', 'three pointers', 'threes'],
  threes: ['3 point', 'three pointers'],
  dd: ['double double'],
  k: ['strikeouts'],
  ks: ['strikeouts'],
  so: ['strikeouts'],
  sog: ['shots on goal'],
  sot: ['shots on target'],
  hr: ['home run'],
  hrs: ['home run'],
  rbi: ['rbis', 'runs batted in'],
  tb: ['total bases'],
  sb: ['stolen bases'],
  ou: ['total'],
  'o/u': ['total'],
  tot: ['total'],
  ats: ['spread'],
  rl: ['spread', 'run line'],
  pl: ['spread', 'puck line'],
  btts: ['both teams to score'],
  gg: ['both teams to score'],
  ags: ['anytime goal'],
  // Periods (#609): a market's period label is searched with its name.
  '1h': ['1st half', 'first half'],
  '2h': ['2nd half', 'second half'],
  '1q': ['1st quarter', 'first quarter'],
  '2q': ['2nd quarter', 'second quarter'],
  '3q': ['3rd quarter', 'third quarter'],
  '4q': ['4th quarter', 'fourth quarter'],
  '1p': ['1st period', 'first period'],
  '2p': ['2nd period', 'second period'],
  '3p': ['3rd period', 'third period'],
  f5: ['1st 5 innings', 'first 5 innings'],
  nrfi: ['1st inning', 'first inning'],
  yrfi: ['1st inning', 'first inning'],
})

const WORD_SPLIT = /[^\p{L}\p{N}]+/u
const SEGMENT_BREAK = ' | '

/** The lowercase words of a text, split on anything but letters and digits. */
export function words(text) {
  return String(text ?? '')
    .toLocaleLowerCase()
    .split(WORD_SPLIT)
    .filter(Boolean)
}

/** The non-blank strings of a list, trimmed; [] for anything but an array. */
export function stringList(values) {
  return Array.isArray(values)
    ? values.map((value) => String(value ?? '').trim()).filter(Boolean)
    : []
}

/**
 * What `searchMarkets` reads of a row: `searchText`, one segment per text
 * (so a phrase never spans the label and an alias), and its words, split once
 * here rather than on every keystroke's match.
 */
export function searchFields(texts) {
  const searchText = texts.map((text) => words(text).join(' ')).filter(Boolean).join(SEGMENT_BREAK)
  return { searchText, searchWords: searchText.split(' ') }
}

/** A catalog value as it is compared everywhere: trimmed, case-insensitively. The key of every Set and Map lookup. */
export const valueKey = (value) => String(value ?? '').trim().toLocaleLowerCase()

/** The Set of `valueKey`s of a list of values. */
export const keySet = (values) => new Set(values.map(valueKey))

const EMPTY = []
/** A saved list field, or one shared empty list (stable for memo deps) when it is not an array. */
export const valueList = (values) => (Array.isArray(values) ? values : EMPTY)
/** A catalog option list (`filterOptions[key]`), or the shared empty list. */
export const optionList = (filterOptions, key) => valueList(filterOptions?.[key])

/** 'player', 'team' or 'game' (anything else) for a catalog `participant_type`. */
export function participantGroup(value) {
  const type = String(value ?? '').trim().toLocaleLowerCase()
  return MARKET_GROUPS.includes(type) ? type : 'game'
}

/**
 * Sorts rows as the list shows them (#837): every full-game row first,
 * then each period's rows together in vocabulary order (`periodIndex`:
 * 1st half, 2nd half, 1st quarter, …), by label within a period as an
 * operator reads them: case-insensitive, numbers in order. A row without
 * a period (an older server's catalog, the book's own markets) sorts with
 * the full game, so a catalog without periods keeps plain label order.
 */
export const byPeriodThenLabel = (left, right) => (left.periodIndex ?? 0) - (right.periodIndex ?? 0)
  || left.label.localeCompare(right.label, undefined, { sensitivity: 'base', numeric: true })

/**
 * Normalizes catalog market options for search and grouping.
 * `hasMetadata` is true when the server sends `participant_type` on any
 * option; then an option without one is a game market (the catalog leaves
 * game markets untyped).
 */
export function normalizeMarketOptions(options) {
  const source = Array.isArray(options) ? options.filter((option) => option && option.value != null && String(option.value).trim()) : []
  const hasMetadata = source.some((option) => typeof option.participant_type === 'string')
  const normalized = source.map((option) => {
    const value = String(option.value).trim()
    const label = String(option.label || '').trim() || value
    const aliases = stringList(option.aliases)
    return {
      value,
      // The value as `valueKey` compares it, for Set and Map lookups.
      key: valueKey(value),
      label,
      participantType: hasMetadata ? participantGroup(option.participant_type) : null,
      family: typeof option.market_family === 'string' ? option.market_family : '',
      statCategory: typeof option.stat_category === 'string' ? option.stat_category : '',
      aliases,
      sports: stringList(option.sports),
      leagues: stringList(option.leagues),
      // Period ids the key can be offered in (#609); [] from an older server.
      periods: stringList(option.periods).map(valueKey),
      // Bet types the key can make (#659): sgp, parlay, straight; [] from an older server.
      betTypes: stringList(option.bet_types).map(valueKey),
      ...searchFields([label, value, ...aliases]),
    }
  })
  return { options: normalized, hasMetadata }
}

// Query tokens. A whitespace-separated token the synonym map knows ("o/u",
// "3pt") stays whole; anything else splits on punctuation like the labels.
function queryTokens(query, synonyms) {
  return String(query ?? '')
    .toLocaleLowerCase()
    .trim()
    .split(WHITESPACE_PATTERN)
    .filter(Boolean)
    .flatMap((token) => (Object.hasOwn(synonyms, token) ? [token] : words(token)))
}

function matchesTerm(option, term) {
  const phrase = words(term).join(' ')
  if (!phrase) return false
  if (phrase.includes(' ')) return ` ${option.searchText}`.includes(` ${phrase}`)
  const searchWords = option.searchWords || option.searchText.split(' ')
  return searchWords.some((word) => word.startsWith(phrase))
}

/**
 * Searches label, value and aliases. Every query token must match: a token
 * matches when it, or one of its synonyms, starts a word of the option
 * (a multi-word synonym matches as a phrase). `expanded` lists the synonyms
 * that found something the typed token alone did not, as "td → touchdown".
 */
export function searchMarkets(options, query, { synonyms = MARKET_SYNONYMS } = {}) {
  const list = Array.isArray(options) ? options : []
  const tokens = queryTokens(query, synonyms || {})
  if (tokens.length === 0) return { matches: list, expanded: [] }

  const expanded = new Set()
  const matches = list.filter((option) => tokens.every((token) => {
    if (matchesTerm(option, token)) return true
    const alternatives = Array.isArray(synonyms?.[token]) ? synonyms[token] : []
    const hit = alternatives.find((alternative) => matchesTerm(option, alternative))
    if (hit) expanded.add(`${token} → ${hit}`)
    return Boolean(hit)
  }))
  return { matches, expanded: [...expanded] }
}

/**
 * Hides options pinned to a sport or league outside the schedule's scope.
 * Options without `sports`/`leagues` are unrestricted and always stay. The
 * server pins market keys by sport only, so a key stays for every league of
 * its sport.
 */
export function scopeMarkets(options, { sports = [], leagues = [], descriptor = null } = {}) {
  return filterOptionsByScope(Array.isArray(options) ? options : [], { sports, leagues, descriptor })
}

/**
 * Splits options into Player / Team / Game markets, each sorted by period,
 * then label (`byPeriodThenLabel`).
 * Returns `{ player, team, game, counts: { player, team, game } }`.
 */
export function groupMarkets(options) {
  const groups = { player: [], team: [], game: [] }
  for (const option of Array.isArray(options) ? options : []) {
    groups[participantGroup(option.participantType)].push(option)
  }
  for (const group of MARKET_GROUPS) groups[group].sort(byPeriodThenLabel)
  return {
    ...groups,
    counts: { player: groups.player.length, team: groups.team.length, game: groups.game.length },
  }
}

/**
 * Chips for the saved `market_keys`, in saved order. A saved value missing
 * from the catalog is kept as `{ value, label: value, unknown: true }` so it
 * can be seen and removed; with `scoped`, a catalog value hidden by the
 * sport scope is flagged `outOfScope`.
 */
export function selectedMarketLabels(values, options, { scoped = null } = {}) {
  const catalog = new Map()
  for (const option of Array.isArray(options) ? options : []) {
    const key = valueKey(option.value)
    if (!catalog.has(key)) catalog.set(key, option)
  }
  const inScope = Array.isArray(scoped) ? new Set(scoped.map((option) => valueKey(option.value))) : null
  return (Array.isArray(values) ? values : []).map((value) => {
    const key = valueKey(value)
    const option = catalog.get(key)
    if (!option) return { value, label: String(value), unknown: true, outOfScope: false, option: null }
    return {
      value,
      label: option.label,
      unknown: false,
      outOfScope: inScope ? !inScope.has(key) : false,
      option,
    }
  })
}
